import React, { useState } from "react";
import { useRouter } from "next/router";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VideocamIcon from "@mui/icons-material/Videocam";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CodeIcon from "@mui/icons-material/Code";
import { useTheme } from "@mui/material";

import Section from "../../../common/components/layout/utils/Section";
import InfoTag from "./common/InfoTag";
import authService from "../../../common/service/authService";
import { neutral } from "../../../common/config/colors";
import {
  FRONTEND_LMS_URL,
  USER_LOGIN_URL,
} from "../../../common/utils/constants";
import NotificationDialog from "../../../common/components/notification/NotificationDialog";

const getIcon = (type: string) => {
  switch (type) {
    case "content":
      return <AssignmentIcon sx={{ fontSize: 16, color: "#666" }} />;
    // case "video":
    //   return <VideocamIcon sx={{ fontSize: 16, color: "#666" }} />;
    case "exercise":
      return <CheckBoxIcon sx={{ fontSize: 16, color: "#666" }} />;
    default:
      return <CodeIcon sx={{ fontSize: 16, color: "#666" }} />;
  }
};

const CourseContent: React.FC<NSCourses.ICourseContentProps> = ({
  modules,
  userEnrolled,
  slug,
}) => {
  const theme = useTheme();
  const [accordian, setAccordian] = useState<number | false>(false);
  const [showLoginNotification, setShowLoginNotification] = useState(false);
  const [showEnrollNotification, setShowEnrollNotification] = useState(false);

  const router = useRouter();

  const handleAccordianChange = (id: number) => {
    if (id === accordian) {
      setAccordian(false);
      return;
    }
    setAccordian(id);
  };

  const handleTopicClick = (id: number) => {
    if (!authService.isAuthenticated) {
      setShowLoginNotification(true);
      return;
    }
    if (userEnrolled) {
      window.location.href = `${FRONTEND_LMS_URL}/${slug}/${id}`;
    } else {
      setShowEnrollNotification(true);
    }
    return;
  };

  const handleLogin = () => {
    window.location.href = `${USER_LOGIN_URL}?next=${router.asPath}`;
  };

  return (
    <Section boxProps={{ pb: 12 }}>
      <>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            pb: 4,
            [theme.breakpoints.down("sm")]: { fontSize: 18 },
          }}
        >
          Course Content
        </Typography>
        <Grid>
          <Grid item xs={12} md={8}>
            {modules.map(({ id, name, topics, topic_type_count }, idx) => (
              <Accordion
                key={id}
                disableGutters
                square
                expanded={accordian === id}
                onChange={() => handleAccordianChange(id)}
                sx={{
                  border: "1px solid #DDE6ED",
                  borderRadius: "5px 5px 0px 0px",
                  boxShadow: "none",
                  background: "##F8F9FE",
                  fontSize: 18,
                  fontWeight: 500,
                  "&:not(:last-child)": {
                    borderBottom: 0,
                  },
                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary
                  aria-controls="panel1d-content"
                  expandIcon={<ExpandMoreIcon />}
                  id={`module-${id}`}
                  sx={{
                    background: "#F8F9FE",
                    "& .MuiAccordionSummary-content": {
                      justifyContent: "space-between",
                      alignItems: "center",
                    },
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      [theme.breakpoints.down("sm")]: {
                        fontSize: theme.spacing(3.5),
                      },
                    }}
                  >
                    {idx + 1}. {name}
                  </Typography>
                  <Box
                    display="flex"
                    fontWeight={{ xs: 400 }}
                    maxWidth="200px"
                    justifyContent="flex-end"
                    sx={{
                      [theme.breakpoints.down("sm")]: {
                        display: "none",
                      },
                    }}
                  >
                    {/* {!!video_count && (
                        <InfoTag
                          isTooltip
                          content={video_count}
                          icon={
                            <VideocamIcon
                              fontSize="small"
                              htmlColor={neutral[300]}
                            />
                          }
                        />
                      )} */}
                    {!!topic_type_count.content && (
                      <InfoTag
                        isTooltip
                        content={topic_type_count.content}
                        icon={
                          <AssignmentIcon
                            fontSize="small"
                            htmlColor={neutral[300]}
                          />
                        }
                      />
                    )}
                    {!!topic_type_count.exercise && (
                      <InfoTag
                        isTooltip
                        content={topic_type_count.exercise}
                        icon={
                          <CheckBoxIcon
                            fontSize="small"
                            htmlColor={neutral[300]}
                          />
                        }
                      />
                    )}
                    {/* {!!practice_count && (
                        <InfoTag
                          isTooltip
                          content={practice_count}
                          icon={
                            <CodeIcon
                              fontSize="small"
                              htmlColor={neutral[300]}
                            />
                          }
                        />
                      )} */}
                    {/* {!!no_of_minutes && (
                        <InfoTag
                          isTooltip
                          content={`(${no_of_minutes} mins)`}
                          boxProps={{
                            minWidth: 100,
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        />
                      )} */}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    py={{ xs: 1, md: 2 }}
                    px={{ xs: 2, md: 3 }}
                    display="flex"
                    justifyContent="flex-start"
                    flexDirection="column"
                  >
                    {topics.map(({ id, name, type }) => (
                      <Box
                        key={id}
                        display="flex"
                        flexDirection="column"
                        py={1.5}
                        sx={{
                          cursor: "pointer",
                        }}
                        onClick={() => handleTopicClick(id)}
                      >
                        <InfoTag
                          content={name}
                          icon={getIcon(type)}
                          typographyProps={{
                            sx: {
                              "&:hover": {
                                color: "#000",
                              },
                            },
                          }}
                          boxProps={{
                            columnGap: 2,
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Grid>
        </Grid>
        <NotificationDialog
          open={showLoginNotification}
          variant="warning"
          agreeText="Login Now"
          onAgree={handleLogin}
          disagreeText="Close"
          onDisagree={() => setShowLoginNotification(false)}
          title="Enroll to the course to view this topic. If you've already enrolled, please login."
        />
        <NotificationDialog
          open={showEnrollNotification}
          variant="warning"
          disagreeText="Close"
          onDisagree={() => setShowEnrollNotification(false)}
          title="Please enroll to the course to view this topic."
        />
      </>
    </Section>
  );
};

export default CourseContent;
